// Components==============
import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import updateAssessment from 'actions/assessment/update';
import RichText from 'components/UI/RichText/RichText';
import { AnimatePresence, motion } from 'framer-motion';
import { AssessmentContext } from 'pages/Feedback/Feedback';
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { interactiveScale } from 'utilities/FramerAnimations';
import './FeedbackItem.module.scss';
import { useRedux } from 'hooks/useRedux';
import FeedbackInput from './components/PeerReflection/FeedbackInput';
// =========================

type AssessmentItemContextType = {
  isChecked: boolean;
  setIsChecked?: React.Dispatch<React.SetStateAction<boolean>>;
  firstPositiveValue: string;
  setFirstPositiveValue: React.Dispatch<React.SetStateAction<string>>;
  firstImprovementValue: string;
  setFirstImprovementValue?: React.Dispatch<React.SetStateAction<string>>;
  secondValue: string;
  setSecondValue: React.Dispatch<React.SetStateAction<string>>;
  firstReviewer: string;
  setFirstReviewer: React.Dispatch<React.SetStateAction<string>>;
  secondReviewer: string;
  setSecondReviewer: React.Dispatch<React.SetStateAction<string>>;
  isEens: boolean;
  setIsEens: React.Dispatch<React.SetStateAction<boolean>>;
  isOneens: boolean;
  setIsOneens: React.Dispatch<React.SetStateAction<boolean>>;
  firstImprovementReviewer: string;
  setFirstImprovementReviewer: React.Dispatch<React.SetStateAction<string>>;
  secondAssessorAgrees: number;
  setSecondAssessorAgrees: React.Dispatch<React.SetStateAction<0 | 1>>;
};

export const AssessmentItemContext = createContext(
  {} as AssessmentItemContextType
);

const SparkItem = ({ sparks }: { sparks: SparkEntity[] }) => {
  const completedSparks = sparks?.filter((spark) => spark.done).length;
  const sparkNotInGoals = sparks?.filter((spark) => spark.notInGoals).length;

  return (
    <div
      styleName={`spark ${
        completedSparks >= sparks?.length
          ? 'done'
          : sparkNotInGoals >= sparks?.length
          ? 'done'
          : ''
      }`}
    >
      {sparkNotInGoals ? (
        <p>
          {sparkNotInGoals} / {sparks?.length}
        </p>
      ) : (
        <p>
          {completedSparks} / {sparks?.length}
        </p>
      )}
    </div>
  );
};

export default function FeedbackItem({
  skill,
  id,
}: {
  skill: AssessorSkillEntity;
  id: number;
}) {
  const ref = useRef<HTMLDivElement>(null);

  const dispatch = useDispatch();

  const [isOpen, setIsOpen] = useState(false);
  const [isFirstRender, setIsFirstRender] = useState(true);
  const userId = useRedux((state) => state.authentication?.user?.id);

  const [isChecked, setIsChecked] = useState(
    !!(skill.pivot.first_assessor_opinion === 1)
  );

  const { toggled, assessment } = useContext(AssessmentContext);
  const isFirstAssessor = assessment?.first_assessor_id === userId;

  const [firstImprovementReviewer, setFirstImprovementReviewer] = useState(
    skill.pivot.first_improvements || ''
  );

  const [firstReviewer, setFirstReviewer] = useState(
    skill.pivot.first_positive_feedback || ''
  );
  const [secondReviewer, setSecondReviewer] = useState(
    skill.pivot.second_feedback || ''
  );

  const [secondAssessorAgrees, setSecondAssessorAgrees] = useState(
    skill.pivot.second_assessor_agrees
  );

  const [isEens, setIsEens] = useState(
    !!(skill.pivot.second_assessor_agrees === 1)
  );

  const [isOneens, setIsOneens] = useState(
    !!(skill.pivot.second_feedback !== '' && isEens === false) || false
  );

  const [firstPositiveValue, setFirstPositiveValue] = useState(
    '' || firstReviewer
  );
  const [firstImprovementValue, setFirstImprovementValue] = useState(
    '' || firstImprovementReviewer
  );
  const [secondValue, setSecondValue] = useState('' || secondReviewer);

  useEffect(() => {
    if (firstPositiveValue !== '') {
      const timerFirst = setTimeout(() => {
        setFirstReviewer(firstPositiveValue);
      }, 1000);

      return () => clearTimeout(timerFirst);
    }
  }, [firstPositiveValue]);

  useEffect(() => {
    if (firstImprovementValue !== '') {
      const timerFirst = setTimeout(() => {
        setFirstImprovementReviewer(firstImprovementValue);
      }, 1000);

      return () => clearTimeout(timerFirst);
    }
  }, [firstImprovementValue]);

  useEffect(() => {
    if (secondValue !== '') {
      const timerSecond = setTimeout(() => {
        setSecondReviewer(secondValue);
      }, 1000);

      return () => clearTimeout(timerSecond);
    }
  }, [secondValue]);

  const approved = isFirstAssessor
    ? skill.pivot.first_assessor_opinion
    : skill.pivot.approved;

  useEffect(() => {
    // @ToDo: prevent firing, only on actual update!
    if (!isFirstRender) {
      dispatch(
        updateAssessment({
          ...assessment,
          first_positive_feedback: firstReviewer,
          first_improvements: firstImprovementReviewer,
          second_feedback: secondReviewer,
          first_assessor_opinion: isChecked ? 1 : 0,
          second_assessor_agrees: secondAssessorAgrees,
          skill_id: skill.id,
          sparks: null,
          assessment_id: assessment?.id || 0,
        })
      );
    }
    setIsFirstRender(false);
  }, [
    isEens,
    isChecked,
    firstReviewer,
    secondReviewer,
    approved,
    secondAssessorAgrees,
  ]);

  useEffect(() => {
    if (toggled) setIsOpen(true);
    if (!toggled) setIsOpen(false);
  }, [toggled]);

  return (
    <AssessmentItemContext.Provider
      value={{
        isChecked,
        setIsChecked,
        firstPositiveValue,
        setFirstPositiveValue,
        firstImprovementValue,
        setFirstImprovementValue,
        secondValue,
        setSecondValue,
        firstReviewer,
        setFirstReviewer,
        firstImprovementReviewer,
        setFirstImprovementReviewer,
        secondReviewer,
        setSecondReviewer,
        isEens,
        setIsEens,
        isOneens,
        setIsOneens,
        secondAssessorAgrees,
        setSecondAssessorAgrees,
      }}
    >
      <div styleName="container">
        <div ref={ref} styleName="scroll-to" />
        <motion.div
          styleName={`wrapper ${isOpen ? 'open' : ''}`}
          {...interactiveScale}
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen((prev) => !prev);
          }}
        >
          {approved ? (
            <div styleName="finish-circle finished">
              <FontAwesomeIcon icon={faCheck} />
            </div>
          ) : (
            <div styleName="finish-circle" />
          )}
          <div styleName="token">
            <img src={`/storage/${skill.icon}`} alt={skill.name} />
            <SparkItem sparks={skill.sparks} />
          </div>
          <div styleName="content">
            <h3 styleName="title">{skill.name}</h3>
            <RichText content={skill.description} />
          </div>
        </motion.div>
        <AnimatePresence>
          {isOpen && <FeedbackInput sparks={skill.sparks} skillId={id} />}
        </AnimatePresence>
      </div>
    </AssessmentItemContext.Provider>
  );
}
